import { ChangeDetectorRef, Component, OnInit, ViewChild, AfterViewChecked } from '@angular/core';
import { CellClickEvent, DataStateChangeEvent, GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { FacilityService } from 'src/app/core/service/facility.service';
import { FacilityListService } from 'src/app/core/service/grid-data/grid-data-facility.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { FacilityEdit } from '../model/facility-edit';

@Component({
  standalone: false,
  selector: 'app-facility-list',
  templateUrl: './facility-list.component.html',
  styleUrls: ['./facility-list.component.scss']
})
export class FacilityListComponent implements OnInit, AfterViewChecked
{
  @ViewChild(GridComponent) public grid: GridComponent;

  public pageSizes = [5, 10, 25, 50, 100];
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public enableAutoExpand = false;
  public gridView: GridDataResult;
  public gridData: any = {};
  public count: number;
  public editDataItem: FacilityEdit;
  public selectedFacility: FacilityEdit;

  /**
   * Facility list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      { field: 'name', dir: 'asc' }
    ]
  };

  public filterText = '';
  public phoneNumberMask: string;
  public isNew = false;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private searchHelperService: SearchHelperService,
    private facilityListService: FacilityListService,
    private appConfigService: AppConfigService,
    private settingService: SettingService,
    private messageHandlerService: MessageHandlerService,
    private facilityService: FacilityService
  )
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.phoneNumberMask = sessionSetting.phoneMask;
      }
    });
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.facilityListService ? this.facilityListService.loading : false;
  }

  public ngOnInit(): void
  {
    this.facilityListService.subscribe({
      next: (value) =>
      {
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.createdOn = new Date(d.createdOn);
            if (d.lastModifiedOn !== null)
            {
              d.lastModifiedOn = new Date(d.lastModifiedOn);
            }
          });
        }
        this.gridView = {
          data: (value) ? this.gridData = value.data : this.gridData = [],
          total: (value) ? this.count = value.total : this.count = 0
        };
      }
    });

    this.getFacilities();
  }

  public ngAfterViewChecked(): void
  {
    this.changeDetectorRef.detectChanges();
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state, 'name');
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.facilityListService.queryByState(state);
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.facilityListService.queryByState(this.state);
  }

  /**
   * Indicates if the grid details visibility. Details will show only if the facility have an address.
   *
   * @param dataItem Grid dataItem
   * @param index Index of the data in the grid list.
   */
  public showDetailsOnlyIfAddress(dataItem: any): boolean
  {
    return dataItem.address &&
      ((dataItem.address.streetLine !== '' && dataItem.address.streetLine !== null) ||
        (dataItem.address.streetLineAdditional !== '' && dataItem.address.streetLineAdditional !== null) ||
        (dataItem.address.city !== '' && dataItem.address.city !== null) ||
        (dataItem.address.postalCode !== '' && dataItem.address.postalCode !== null) ||
        dataItem.address.state !== null ||
        dataItem.address.country !== null);
  }

  /**
   * Event fires when a task is selected.
   */
  public onCellClick = (event: CellClickEvent): void =>
  {
    this.collapseAllRows();
    if (this.enableAutoExpand)
    {
      event.sender.expandRow(event.rowIndex);
    }
  };

  /**
   * Add facility handler
   */
  public addHandler(): void
  {
    this.isNew = true;
    setTimeout(() =>
    {
      this.editDataItem = new FacilityEdit();
    });
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Save facility handler.
   *
   * @param facility Facility object to save.
   */
  public saveChangesHandler(facility: FacilityEdit): void
  {
    this.facilityService.saveFacility(facility, this.isNew, facility.id).subscribe({
      next: () =>
      {
        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('FACILITY-SAVED-SUCCESSFULLY');
        this.getFacilities();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Cancel facility changes.
   */
  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Edit handler
   *
   * @param id Facility id.
   */
  public editHandler(id: number): void
  {
    this.isNew = false;
    this.facilityService.getFacilityById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    // Check if in use
    this.facilityService.existsFacility(id).subscribe({
      next: (inUseResponse) =>
      {
        if (inUseResponse)
        {
          this.messageHandlerService.displayWarning('FACILITY-IN-USE-CANNOT-DELETE');
        }
        else
        {
          this.facilityService.getFacilityById(id).subscribe({
            next: (data) =>
            {
              this.selectedFacility = data as FacilityEdit;
              this.open('deleteDialog');
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const facilityId = this.selectedFacility.id;
      this.facilityService.deleteFacility(facilityId).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('FACILITY-DELETED-SUCCESSFULLY');
          this.getFacilities();
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedFacility = null;
  }

  /**
   * Get facilities from API per the applied filter.
   */
  private getFacilities(): void
  {
    this.state.skip = 0;
    this.facilityListService.queryByState(this.state);
  }

  /**
   * Collapse all task details opened.
   */
  private collapseAllRows()
  {
    if (this.grid === undefined)
    {
      return;
    }
    // Will collapse all preceeding task details based on the page size and current page.
    const maxLength = this.appConfigService.getConfig().pageSize + this.state.skip;
    for (let i = 0; i < maxLength; i++)
    {
      this.grid.collapseRow(i);
    }
  }
}
